// a button that optionally shows its children as a menu when clicked

import React, { useRef, useEffect } from 'react';
import styled from 'styled-components';
import { LucideProps } from 'lucide-react';
import { colors, ui } from '@annote/core/styles';
import { MenuProps } from './MenuComponents';

const ButtonWrapper = styled.div`
  position: relative;
  display: inline-block;
`;

const IconButton = styled.button<{ isSelected?: boolean }>`
  background: none;
  border: none;
  padding: 4px;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  border-radius: 4px;
  background-color: ${(props) => (props.isSelected ? colors.button.selected : 'transparent')};
  &:hover {
    background-color: ${colors.button.hover};
  }
`;

interface CardMenuButtonProps {
  iconSVG: React.ComponentType<React.SVGProps<SVGSVGElement>>;
  children?: React.ReactNode;
  isOpen: boolean;
  onToggle: () => void;
}

export const CardMenuButton: React.FC<CardMenuButtonProps> = ({ iconSVG, children, isOpen, onToggle }) => {
  const buttonRef = useRef<HTMLDivElement>(null);

  const handleClick = (e: React.MouseEvent): void => {
    e.stopPropagation();
    if (children) {
      onToggle();
    }
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (!isOpen) return;

      const target = event.target as Node;

      // Check if click is inside the button
      if (buttonRef.current && buttonRef.current.contains(target)) return;

      // Check if click is inside a portal menu
      const portalMenu = (target as Element).closest?.('[data-portal-menu="true"]');
      if (portalMenu) return;

      onToggle();
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [isOpen, onToggle]);

  const IconComponent = iconSVG;
  const iconSize = ui.getIconSizePx('cardButton');
  return (
    <ButtonWrapper ref={buttonRef}>
      <IconButton onClick={handleClick} isSelected={isOpen}>
        <IconComponent
          width={iconSize}
          height={iconSize}
          stroke={colors.icon.primary}
          strokeWidth={ui.iconStrokeWidth}
        />
      </IconButton>
      {isOpen &&
        React.isValidElement(children) &&
        React.cloneElement(children as React.ReactElement<MenuProps>, {
          closeMenu: () => onToggle(),
          triggerRef: buttonRef as React.RefObject<HTMLElement>,
        })}
    </ButtonWrapper>
  );
};

interface CardButtonProps {
  iconSVG: React.ComponentType<React.SVGProps<SVGSVGElement> | LucideProps>;
  onClick: () => void;
  className?: string;
}

// Look sthe same as card Menu Button but without the menu
export const CardButton = React.forwardRef<HTMLDivElement, CardButtonProps>(({ iconSVG, onClick, className }, ref) => {
  const IconComponent = iconSVG;
  const iconSize = ui.getIconSizePx('cardButton');
  return (
    <ButtonWrapper ref={ref} className={className}>
      <IconButton onClick={onClick}>
        <IconComponent
          width={iconSize}
          height={iconSize}
          stroke={colors.icon.primary}
          strokeWidth={ui.iconStrokeWidth}
        />
      </IconButton>
    </ButtonWrapper>
  );
});

export default CardMenuButton;
